import { HexColor, QuickAction } from '@moojo/domain-types'
import { Instant } from '@moojo/duration'
import { z } from 'zod'

import { DspEntityId } from './dsp-entity-id'

export const LiveAdPlacement = z.object({
  /**
   * Domains where this ad is allowed to be displayed.
   * @example ["example.com", "shop.mystore.com"]
   */
  allowedDomains: z.string().array(),

  /**
   * Languages of sites/pages where this ad can appear.
   * @example 'any' // Any language
   * @example ['en', 'es'] // Only English and Spanish sites
   */
  allowedLanguages: z.literal('any').or(z.string().array()),

  /**
   * Regular expression pattern to match URL paths where this ad should be displayed (optional).
   *
   * @example ".*" // All pages
   * @example "/products/.*" // All product pages
   * @example "/(category|shop)/.*" // Category or shop pages
   * @example "/blog/[0-9]+/.*" // Blog post pages
   */
  urlPattern: z.string(),
  /**
   * Whether this ad should be used as a fallback when no other ads match.
   * Fallback ads are shown when no regular ads match the placement criteria.
   */
  isFallback: z.boolean().default(false),
})

export type LiveAdPlacement = z.infer<typeof LiveAdPlacement>

// TODO(roman): consider partial of
export const LiveAdBrandData = z
  .object({
    logoUrl: z.string().url(),
    websiteUrl: z.string().url(),
    productFeedUrl: z.string().url(),
    socialMediaUrl: z.string().url(),
    primaryColor: HexColor,
    secondaryColor: HexColor,
    fontColor: HexColor,
  })
  .partial()

export type LiveAdBrandData = z.infer<typeof LiveAdBrandData>

export const LiveAd = z.object({
  /**
   * The ID of the revision this LiveAd was compiled from
   */
  adRevisionId: z.string().optional(),
  /**
   * The ID of the ad (on the DSP)
   */
  id: DspEntityId,

  /**
   * The ID of the campaign (on the DSP)
   */
  campaignId: DspEntityId,

  /**
   * The organization ID that owns this ad (optional for backward compatibility)
   */
  orgId: DspEntityId.optional(),

  /**
   * Placement rules - where this ad can appear
   */
  placement: LiveAdPlacement,

  /**
   * ID used for tracking this ad.
   */
  adAnalyticsId: z.string(),

  /**
   * Ad can only be shown after this time
   */
  startTime: Instant.zod(),

  /**
   * Ad cannot be shown after this time
   */
  endTime: Instant.zod(),

  /**
   * The brand name or company behind the product.
   *
   * This appears prominently in the ad and helps with brand recognition.
   * Should be the official brand name as it appears in marketing materials.
   *
   * @example "Apple", "Nike", "Samsung"
   */
  brandName: z.string(),

  /**
   * Primary headline text that captures user attention.
   *
   * This is the main title displayed in the advertisement and should be
   * compelling and concise. Typically the most prominent text element
   * that users see first when viewing the ad.
   *
   * @example "iPhone 15 Pro - Now Available", "Summer Sale - Up to 50% Off"
   */
  headline: z.string(),

  /**
   * Secondary headline or subtitle for additional context.
   *
   * Provides supporting information to the main headline, often used
   * for additional details, product features, or to create urgency.
   * Should complement the main headline without being redundant.
   *
   * @example "Free shipping on orders over $50", "Limited time offer"
   */
  subHeadline: z.string(),

  /**
   * URL of the product image to display in the advertisement.
   *
   * Should be a high-quality image that represents the product well.
   * Must be accessible via HTTPS for security.
   *
   * @example "https://cdn.example.com/products/iphone-15-pro.jpg"
   */
  imageUrl: z.string().url(),

  /**
   * The destination URL where users will be redirected when they click the ad.
   *
   * This should be a valid, accessible URL that leads to the product page,
   * landing page, or relevant conversion funnel. Must include protocol (http/https).
   *
   * @example "https://store.apple.com/iphone-15-pro"
   */
  landingUrl: z.string().url(),

  /**
   * The text displayed on the call-to-action button.
   *
   * Examples:
   * - `Learn More`: For informational or consideration-stage ads
   * - `Shop Now`: For direct purchase or commerce-focused ads
   * - `Get Started`: For service sign-ups or trial offers
   *
   * @example "shopNow" for e-commerce, "learnMore" for B2B services
   */
  callToActionText: z.string(),

  /**
   * Text displayed in the ad launcher or initial display element.
   *
   * This is the preview text shown before the full ad expands or loads.
   * Acts as a teaser to encourage user interaction. Should be compelling
   * and give users a reason to engage with the full advertisement.
   * Must be non-empty to ensure the launcher has visible content.
   *
   * @example "Discover Amazing Deals"
   * @example "New Arrivals Inside"
   * @example "Click to Explore"
   */
  launcherText: z.string().min(1),

  /** Text to be displayed as a hint to the user in the prompt input field. */
  placeholderText: z.string().optional(),

  /** Title of the entire agent widget ("banner title" in voiceflow terminology) */
  widgetHeaderText: z.string().optional(),

  /**
   * Complete brand information and visual identity data.
   *
   * Contains all the brand-specific styling and identity elements
   * needed to render the ad with consistent brand appearance.
   * Includes logo, colors, and URLs that represent the brand.
   */
  brand: LiveAdBrandData,

  /**
   * Content guidelines and restrictions for the ad agent's responses.
   *
   * Defines boundaries for what the AI agent should and shouldn't discuss.
   * Helps ensure responses stay on-brand, appropriate, and focused on
   * the product/service being advertised. Maximum 1000 characters.
   *
   * @example "Stay focused on product features. Avoid discussing competitors."
   * @example "Be helpful and friendly. Don't make medical claims."
   */
  guardrails: z.string(),

  /**
   * Automated follow-up message sent after user interactions.
   *
   * Optional message that appears after a conversation to encourage
   * further engagement, provide additional information, or guide users
   * toward a conversion action. Should be concise and actionable.
   *
   * @example "Thanks for chatting! Ready to make a purchase?"
   * @example "Need more help? Visit our website for details."
   */
  followupMessage: z.string(),

  /**
   * Pre-defined quick action buttons shown after the follow-up message.
   *
   * Array of clickable button options that appear after the follow-up message
   * to help users quickly access common information. These buttons provide
   * easy entry points into specific conversation topics about the advertised
   * product or brand.
   *
   * Available actions:
   * - `aboutTheBrand`: Information about the company/brand
   * - `similarBrandProducts`: Other products from the same brand
   * - `aboutTheProduct`: Detailed product information
   * - `followUsOnSocialMedia`: Follow us on social media
   *
   */
  quickActions: QuickAction.array(),
})

export type LiveAd = z.infer<typeof LiveAd>
